/** Tenant roster table driven by saas-auth identity with a slug-keyed ops overlay. */
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { StatusPill } from '@/components/ui';
import type { TenantDto } from '@/api/saas/types';
import type { PlatformTenantSummary } from '@/api/wsr/types';
import { EnablesPips } from './EnablesPips';

export interface TenantRosterRow {
  identity: TenantDto;
  ops?: PlatformTenantSummary | undefined;
}

interface TenantsTableProps {
  tenants: TenantRosterRow[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  emptyMessage: string;
}

function slugInitials(slug: string): string {
  const parts = slug.split('-').filter((part) => part.length > 0);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function TenantsTable({
  tenants,
  loading,
  error,
  onRetry,
  emptyMessage,
}: TenantsTableProps) {
  const { t } = useCopy();

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Typography
            component="button"
            variant="caption"
            onClick={onRetry}
            sx={{
              border: 0,
              background: 'none',
              cursor: 'pointer',
              textDecoration: 'underline',
              color: 'inherit',
            }}
          >
            {t('common.retry', 'Retry')}
          </Typography>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" aria-busy={loading ? 'true' : 'false'}>
      <Table aria-label={t('tenants.tableAriaLabel', 'Tenants roster')} size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>{t('tenants.col.tenant', 'Tenant')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t('tenants.col.status', 'Status')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t('tenants.col.wstudio', 'wStudio')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t('tenants.col.enables', 'Enables')}</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>{t('tenants.col.lastSync', 'Last sync')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('common.loading', 'Loading')}
                </Typography>
              </TableCell>
            </TableRow>
          ) : tenants.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            tenants.map(({ identity, ops }) => (
              <TableRow key={identity.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                      {slugInitials(identity.slug)}
                    </Avatar>
                    <Box>
                      <Link
                        component={RouterLink}
                        to={`/tenants/${identity.id}`}
                        variant="body2"
                        underline="hover"
                      >
                        {identity.name}
                      </Link>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {identity.slug}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <StatusPill status={identity.status} />
                </TableCell>
                {ops ? (
                  <>
                    <TableCell>
                      <Chip
                        label={
                          ops.wstudioConfigured
                            ? t('tenants.wstudio.configured', 'Configured')
                            : t('tenants.wstudio.notConfigured', 'Not configured')
                        }
                        color={ops.wstudioConfigured ? 'success' : 'default'}
                        variant={ops.wstudioConfigured ? 'filled' : 'outlined'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <EnablesPips
                        global={ops.globalEnabled}
                        tenant={ops.tenantEnabled}
                        services={ops.services}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {ops.lastSyncAt
                          ? new Date(ops.lastSyncAt).toLocaleString()
                          : t('tenants.lastSync.never', '—')}
                      </Typography>
                    </TableCell>
                  </>
                ) : (
                  <TableCell colSpan={3}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t('tenants.opsNotProvisioned', 'Ops not provisioned')}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
